import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { ErrorNotice } from '../../../components/ErrorNotice';
import { LoadingIndicator } from '../../../components/Spinner';
import { formatDateTime } from '../../../i18n';
import { useSession } from '../../auth/queries';
import { nameOf, useAssignableUsers } from '../../users/queries';
import { useApprovals, useCancelApproval, useCreateApproval, useDecideApproval } from './queries';
import { MultiCombobox } from '../../../components/MultiCombobox';
import { Select } from '../../../components/Select';

/**
 * Genehmigungen am Ticket -- F3-17, K-63.
 *
 * **Der Knopf zum Entscheiden erscheint nur bei dem, der gefragt ist.** Das
 * ist Bequemlichkeit und keine Absicherung: Der Server ermittelt den Schritt
 * ohnehin aus der Sitzung und antwortet jedem anderen mit 404. Die Anzeige
 * spart dem Bearbeiter nur den Versuch.
 *
 * Wer in Vertretung entscheidet, sieht denselben Knopf -- ihm wird die
 * Vertretung nicht angekuendigt, weil sie fuer ihn nichts aendert: Er
 * entscheidet, und im Nachweis steht danach, fuer wen.
 */
interface ApprovalPanelProps {
  ticketId: string;
  canRequest: boolean;
}

export function ApprovalPanel({ ticketId, canRequest }: ApprovalPanelProps) {
  const { t } = useTranslation();
  const session = useSession();
  const approvals = useApprovals(ticketId);
  const users = useAssignableUsers();
  const create = useCreateApproval(ticketId);
  const decide = useDecideApproval(ticketId);
  const cancel = useCancelApproval(ticketId);

  const [subject, setSubject] = useState('');
  const [approvers, setApprovers] = useState<string[]>([]);
  const [ratio, setRatio] = useState('1');
  const [comment, setComment] = useState('');

  const userId = session.data?.user_id;
  const running = (approvals.data ?? []).find((request) => request.status === 'pending');

  return (
    <div className="stack">
      {approvals.isPending ? <LoadingIndicator /> : null}
      {approvals.error ? <ErrorNotice error={approvals.error} /> : null}

      {(approvals.data ?? []).length === 0 ? (
        <p className="table__muted">{t('approvals.none')}</p>
      ) : null}

      {(approvals.data ?? []).map((request) => {
        // Der eigene offene Schritt der laufenden Stufe -- nur dann wird
        // entschieden. Bei einer Vertretung steht hier nichts; der Server
        // laesst sie trotzdem zu, und der Knopf erscheint ueber die
        // Warteliste (siehe /approvals/pending).
        const steps = request.steps ?? [];
        const mine = steps.find(
          (step) =>
            step.stage === request.current_stage &&
            step.decision === 'pending' &&
            step.approver_id === userId,
        );

        return (
          <section className="card" key={request.id}>
            <div className="row">
              <strong>{request.subject}</strong>
              <Badge
                tone={
                  request.status === 'approved'
                    ? 'success'
                    : request.status === 'rejected'
                      ? 'danger'
                      : request.status === 'cancelled'
                        ? 'neutral'
                        : 'info'
                }
              >
                {t(`approvals.status.${request.status}`)}
              </Badge>
              {request.status === 'pending' ? (
                <span className="table__muted">
                  {t('approvals.stage', { stage: request.current_stage })}
                </span>
              ) : null}
            </div>

            <ol className="task-list">
              {steps.map((step) => (
                <li key={step.id} className="task-list__item">
                  <Badge tone="neutral">{t('approvals.stage', { stage: step.stage })}</Badge>
                  <span>{nameOf(users.data, step.approver_id)}</span>
                  <Badge
                    tone={
                      step.decision === 'approved' || step.decision === 'delegated'
                        ? 'success'
                        : step.decision === 'rejected'
                          ? 'danger'
                          : 'neutral'
                    }
                  >
                    {t(`approvals.decision.${step.decision}`)}
                  </Badge>

                  {/* Der Nachweis: Wer gefragt war, steht links; wer
                      entschieden hat, hier -- und beides faellt bei einer
                      Vertretung auseinander. */}
                  {step.decided_by && step.decided_by !== step.approver_id ? (
                    <span className="table__muted">
                      {t('approvals.decidedBy', { name: nameOf(users.data, step.decided_by) })}
                    </span>
                  ) : null}

                  {step.comment ? <span className="table__muted">{step.comment}</span> : null}
                  {step.decided_at ? (
                    <span className="table__muted">{formatDateTime(step.decided_at)}</span>
                  ) : null}
                </li>
              ))}
            </ol>

            {mine ? (
              <div className="stack">
                <input
                  className="field__control"
                  aria-label={t('approvals.comment')}
                  placeholder={t('approvals.commentHint')}
                  value={comment}
                  onChange={(event) => {
                    setComment(event.target.value);
                  }}
                />
                <div className="row">
                  <Button
                    variant="primary"
                    disabled={decide.isPending}
                    onClick={() => {
                      decide.mutate(
                        { requestId: request.id, approved: true, comment: comment || null },
                        {
                          onSuccess: () => {
                            setComment('');
                          },
                        },
                      );
                    }}
                  >
                    {t('approvals.approve')}
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={decide.isPending}
                    onClick={() => {
                      decide.mutate(
                        { requestId: request.id, approved: false, comment: comment || null },
                        {
                          onSuccess: () => {
                            setComment('');
                          },
                        },
                      );
                    }}
                  >
                    {t('approvals.reject')}
                  </Button>
                </div>
                <p className="field__hint">{t('approvals.rejectHint')}</p>
              </div>
            ) : null}

            {decide.error ? <ErrorNotice error={decide.error} /> : null}

            {request.status === 'pending' && canRequest ? (
              <Button
                variant="ghost"
                onClick={() => {
                  cancel.mutate(request.id);
                }}
              >
                {t('approvals.cancel')}
              </Button>
            ) : null}
          </section>
        );
      })}

      {canRequest && !running ? (
        <section className="card">
          <h3>{t('approvals.request')}</h3>

          <div className="field">
            <label className="field__label" htmlFor="approval-subject">
              {t('approvals.subject')}
            </label>
            <input
              id="approval-subject"
              className="field__control"
              value={subject}
              onChange={(event) => {
                setSubject(event.target.value);
              }}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="approval-approvers">
              {t('approvals.approvers')}
            </label>
            {/* Mehrere Entscheider als Marken -- die Mehrfachauswahl davor
                verlor bei einem Klick ohne Steuerungstaste alles Gewaehlte. */}
            <MultiCombobox
              id="approval-approvers"
              value={approvers}
              onChange={setApprovers}
              options={(users.data ?? []).map((user) => ({ id: user.id, label: user.full_name }))}
            />
            <span className="field__hint">{t('approvals.approversHint')}</span>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="approval-ratio">
              {t('approvals.ratio')}
            </label>
            <Select
              id="approval-ratio"
              className="field__control"
              value={ratio}
              onChange={(event) => {
                setRatio(event.target.value);
              }}
            >
              <option value="1">{t('approvals.ratioAll')}</option>
              <option value="0.5">{t('approvals.ratioHalf')}</option>
            </Select>
          </div>

          {create.error ? <ErrorNotice error={create.error} /> : null}

          <Button
            variant="primary"
            disabled={subject.trim() === '' || approvers.length === 0 || create.isPending}
            onClick={() => {
              create.mutate(
                {
                  subject: subject.trim(),
                  stages: [{ approver_ids: approvers, required_ratio: Number(ratio) }],
                },
                {
                  onSuccess: () => {
                    setSubject('');
                    setApprovers([]);
                  },
                },
              );
            }}
          >
            {t('approvals.request')}
          </Button>
        </section>
      ) : null}
    </div>
  );
}
